import { useEffect, useState } from "react";
import {
  createDatabase,
  storeDataInIndexedDB,
  fetchDataFromIndexedDB,
  deleteDataFromIndexedDB,
} from "./IndexedDBUtils";

export const householdOfflineDB = createDatabase("householdOfflineDB", "householdOfflineStore");
export const householdOfflineContactsDB = createDatabase(
  "householdOfflineContactsDB",
  "householdOfflineContactsStore"
);
export const cityOptionsDB = createDatabase("cityOptionsDB", "cityOptionsStore");
export const dataPropertyDB = createDatabase("dataPropertyDB", "dataPropertyStore");
export const userListDB = createDatabase("userListDB", "userListStore");

export const useOnlineStatus = () => {
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    let checkTimeout;
    let isChecking = false;

    const checkServiceWorkerOnline = async () => {
      if (isChecking) return;
      isChecking = true;

      try {
        // Attempt a quick HEAD request to detect service worker offline.
        // Only check when navigator.onLine is true but we suspect SW is offline.
        if (navigator.onLine) {
          const response = await fetch(window.location.href, {
            method: 'HEAD',
            cache: 'no-store',
            timeout: 3000
          });
          setOnline(response.ok);
        }
      } catch (e) {
        setOnline(false);
      } finally {
        isChecking = false;
      }
    };

    const handleOnline = () => {
      setOnline(true);
      // Verify service worker is actually responsive when coming back online
      clearTimeout(checkTimeout);
      checkTimeout = setTimeout(checkServiceWorkerOnline, 500);
    };

    const handleOffline = () => {
      setOnline(false);
      clearTimeout(checkTimeout);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearTimeout(checkTimeout);
    };
  }, []);

  return online;
};

export const saveHouseholdSnapshot = async (dataArray) => {
  // bulkPut (inside storeDataInIndexedDB) upserts by "id" - records already
  // cached from an earlier filter/save stay untouched, and this call's
  // records are added/updated alongside them. Do NOT clear the store first:
  // that would wipe out households saved under a previous filter (e.g. one
  // city saved, then a different city saved) instead of accumulating both.
  return storeDataInIndexedDB(householdOfflineDB, "householdOfflineStore", dataArray);
};

export const loadHouseholdSnapshot = () => {
  return fetchDataFromIndexedDB(householdOfflineDB, "householdOfflineStore");
};

export const clearHouseholdSnapshot = () => {
  return deleteDataFromIndexedDB(householdOfflineDB, "householdOfflineStore");
};

export const saveHouseholdContactsSnapshot = async (householdID, contacts) => {
  return storeDataInIndexedDB(householdOfflineContactsDB, "householdOfflineContactsStore", {
    id: householdID,
    contacts,
  });
};

export const loadHouseholdContactsSnapshot = async (householdID) => {
  const all = await fetchDataFromIndexedDB(householdOfflineContactsDB, "householdOfflineContactsStore");
  const found = Array.isArray(all) ? all.find((c) => c.id === householdID) : null;
  return found ? found.contacts : null;
};

export const clearHouseholdContactsSnapshot = () => {
  return deleteDataFromIndexedDB(householdOfflineContactsDB, "householdOfflineContactsStore");
};

// City dropdown options power the filter modal's city search on both the
// Household List and Canvass Map pages, but were only ever fetched live from
// /getCity - with no cache, the city filter was unusable offline. Cache them
// as a single record (there's only ever one options list) so the filter
// dropdown still has choices when offline.
export const saveCityOptions = (options) => {
  return storeDataInIndexedDB(cityOptionsDB, "cityOptionsStore", { id: "cities", options });
};

export const loadCityOptions = async () => {
  const all = await fetchDataFromIndexedDB(cityOptionsDB, "cityOptionsStore");
  const found = Array.isArray(all) ? all.find((c) => c.id === "cities") : null;
  return found ? found.options : [];
};

// dataProperty holds HubSpot field metadata (options for select fields like
// presence_of_children_code and residence_household_gender_composition) and,
// like city options, was only ever fetched live - with no cache those
// dropdowns rendered empty if the form was opened while already offline.
export const saveDataProperties = (properties) => {
  return storeDataInIndexedDB(dataPropertyDB, "dataPropertyStore", { id: "dataProperty", properties });
};

export const loadDataProperties = async () => {
  const all = await fetchDataFromIndexedDB(dataPropertyDB, "dataPropertyStore");
  const found = Array.isArray(all) ? all.find((p) => p.id === "dataProperty") : null;
  return found ? found.properties : [];
};

// The owner/user list powers the "last door knocked by" select, and like
// dataProperty was only ever fetched live from /ownerlist - with no cache
// that dropdown rendered empty if the form was opened while already offline.
// Keyed per tenant so a cached owner list never carries over after a tenant switch.
export const userListKey = () => `userList:${localStorage.getItem("currentTenantId") || "none"}`;

export const saveUserList = (users) => {
  return storeDataInIndexedDB(userListDB, "userListStore", { id: userListKey(), users });
};

export const loadUserList = async () => {
  const key = userListKey();
  const all = await fetchDataFromIndexedDB(userListDB, "userListStore");
  const found = Array.isArray(all) ? all.find((u) => u.id === key) : null;
  return found ? found.users : [];
};
